import React, { useState, useEffect } from 'react';
import { Text } from '../Text';
import './styles.css';

const DatePicker = ({ 
  value, 
  onChange, 
  error,
  minYear = 1900,
  maxYear = new Date().getFullYear(),
  translations = {
    day: 'Jour',
    month: 'Mois',
    year: 'Année'
  },
  monthNames = {
    fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  },
  locale = 'fr',
  className = ''
}) => {
  // États locaux pour jour, mois, année
  const [day, setDay] = useState(value ? value.getDate() : '');
  const [month, setMonth] = useState(value ? value.getMonth() + 1 : '');
  const [year, setYear] = useState(value ? value.getFullYear() : '');
  
  // Fonction pour obtenir un objet Date à partir des sélections
  const getSelectedDate = () => {
    if (!day || !month || !year) return null;
    return new Date(year, month - 1, day);
  };
  
  // Mettre à jour les états locaux lorsque la valeur externe change
  useEffect(() => {
    if (value instanceof Date && !isNaN(value.getTime())) {
      setDay(value.getDate());
      setMonth(value.getMonth() + 1);
      setYear(value.getFullYear());
    } else {
      setDay('');
      setMonth('');
      setYear('');
    }
  }, [value]);
  
  // Mettre à jour la valeur externe lorsque les états locaux changent
  useEffect(() => {
    if (day && month && year) {
      const newDate = getSelectedDate();
      if (newDate && !isNaN(newDate.getTime()) && onChange) {
        onChange(newDate);
      }
    } else if (onChange && (value !== null && value !== undefined)) {
      // Si l'un des champs est vide et qu'une valeur existait avant, on la réinitialise
      onChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, month, year]);
  
  // Générer les options pour les jours (1-31)
  const getDayOptions = () => {
    const daysInMonth = month ? new Date(year || 2020, month, 0).getDate() : 31;
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };
  
  // Générer les options pour les mois (1-12)
  const getMonthOptions = () => {
    const names = monthNames[locale] || monthNames.fr;
    return names.map((name, index) => ({ value: index + 1, label: name }));
  };
  
  // Générer les options pour les années (de minYear à maxYear)
  const getYearOptions = () => {
    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);
  };
  
  return (
    <div className={`datepicker-container ${className}`}>
      <div className="datepicker-select-container">
        {/* Sélecteur de jour */}
        <div className="datepicker-select-group">
          <select 
            className="datepicker-select"
            value={day} 
            onChange={(e) => setDay(e.target.value ? parseInt(e.target.value, 10) : '')}
            aria-label={translations.day}
          >
            <option value="">{translations.day}</option>
            {getDayOptions().map((d) => (
              <option key={`day-${d}`} value={d}>{d}</option>
            ))}
          </select>
        </div>
        
        {/* Sélecteur de mois */}
        <div className="datepicker-select-group">
          <select 
            className="datepicker-select"
            value={month} 
            onChange={(e) => setMonth(e.target.value ? parseInt(e.target.value, 10) : '')}
            aria-label={translations.month}
          >
            <option value="">{translations.month}</option>
            {getMonthOptions().map((m) => (
              <option key={`month-${m.value}`} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        
        {/* Sélecteur d'année */}
        <div className="datepicker-select-group">
          <select 
            className="datepicker-select"
            value={year} 
            onChange={(e) => setYear(e.target.value ? parseInt(e.target.value, 10) : '')}
            aria-label={translations.year}
          >
            <option value="">{translations.year}</option>
            {getYearOptions().map((y) => (
              <option key={`year-${y}`} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>
      
      {error && (
        <div className="datepicker-error">
          <Text variant="caption" color="error">
            {error}
          </Text>
        </div>
      )}
    </div>
  );
};

export { DatePicker }; 